import { authService } from '../../services/auth/authService';
import { SESSION_STALE_THRESHOLD_MS } from '../../constants/authConstants';

/**
 * IsReauthRequiredUseCase
 *
 * Answers "is the current Firebase session too stale to allow an account
 * deletion without a fresh sign-in?" — the client-side re-auth gate the
 * Delete Account page uses to decide whether to show login buttons.
 *
 * WEB-ONLY rule (no Flutter source; the Flutter delete flow does not
 * re-authenticate — see `SESSION_STALE_THRESHOLD_MS`). It maps the four
 * adaptive states from the task prompt
 * (`workflow/task_prompts/feat_delete_account_page_task_prompt.md` lines 51-53):
 *
 * - Not signed in            → `lastSignIn` is `null` → `false` (login is
 *                              needed, but that is decided by auth state, not
 *                              by staleness; "not accessible → treat as fresh").
 * - Last-sign-in NOT readable → `null` → `false` (treat as fresh, line 53).
 * - Signed in, older than 30d → `true`  (force a fresh sign-in, line 52).
 * - Signed in, within 30d     → `false` (no re-auth needed).
 *
 * Staleness is measured from Firebase Auth's `lastSignInTime`
 * (`authService.getLastSignInTimestamp()`), NOT the user doc's
 * `last_seen_timestamp` — the latter is bumped on every app-open by
 * `LastSeenTimestampListener` and would almost never read as stale.
 *
 * Synchronous — no I/O.
 */
export class IsReauthRequiredUseCase {
  execute(): boolean {
    const lastSignIn = authService.getLastSignInTimestamp();
    if (lastSignIn === null) {
      // Not signed in or timestamp not accessible → treat as fresh.
      return false;
    }

    // Strict `>` matches "older than 30 days" (exactly at the boundary is not
    // yet stale).
    return Date.now() - lastSignIn > SESSION_STALE_THRESHOLD_MS;
  }
}

export const isReauthRequiredUseCase = new IsReauthRequiredUseCase();
